'use client';

import { ColumnsDropdown } from './ResultsTableParts';
import { AddToListMenu } from '@/new_components/lists/AddToListMenu';
import { Button } from '@/components/ui/button';
import { Download, Globe, Users } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { RowSelectionState, VisibilityState } from '@tanstack/react-table';
import type { CompanySearchResult } from '@/lib/actions/company-search';

interface ResultsToolbarProps {
  t: TFunction<'translation', undefined>;
  country: string | undefined;
  columnVisibility: VisibilityState;
  handleColumnVisibilityChange: (id: string, visible: boolean) => void;
  canCustomizeColumns: boolean;
  openProDialog: (feature: 'export' | 'columns' | 'enrich') => void;
  selectedEuIds: string[];
  displayedResults: CompanySearchResult[];
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  handleExportClick: () => void;
  results: CompanySearchResult[];
  handleEnrichWebsites: () => Promise<void>;
  isEnrichingWebsites: boolean;
  handleEnrichEmployees: () => Promise<void>;
  isEnrichingEmployees: boolean;
}

export function ResultsToolbar({
  t,
  country,
  columnVisibility,
  handleColumnVisibilityChange,
  canCustomizeColumns,
  openProDialog,
  selectedEuIds,
  displayedResults,
  setRowSelection,
  handleExportClick,
  results,
  handleEnrichWebsites,
  isEnrichingWebsites,
  handleEnrichEmployees,
  isEnrichingEmployees,
}: ResultsToolbarProps) {
  return (
<div className="hidden lg:block border-b bg-muted/5 shrink-0">
          <div className="flex items-center gap-1.5 px-2 lg:px-3 py-1.5">
            {/* Columns picker is shown to everyone; customizing is Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <ColumnsDropdown
              t={t}
              country={country}
              columnVisibility={columnVisibility}
              onChange={handleColumnVisibilityChange}
              locked={!canCustomizeColumns}
              onLockedClick={() => openProDialog('columns')}
            />
            {/* Add-to-list: the whole reason row selection exists. With
                nothing ticked it adds every company on this page, which is
                what "add these results to a list" means to someone who has
                just filtered the register. */}
            <AddToListMenu
              euIds={
                selectedEuIds.length
                  ? selectedEuIds
                  : displayedResults.map((r) => r.euId)
              }
              className="h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
              label={
                selectedEuIds.length
                  ? t('lists.addTo.buttonMany', {
                      count: selectedEuIds.length,
                      defaultValue: 'Add {{count}} to list',
                    })
                  : t('lists.addTo.buttonPage', {
                      count: displayedResults.length,
                      defaultValue: 'Add page to list',
                    })
              }
              onAdded={() => setRowSelection({})}
            />
            {selectedEuIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto min-h-7 py-1 text-[11px] rounded-full text-muted-foreground"
                onClick={() => setRowSelection({})}
              >
                {t('search.clearSelection', {
                  count: selectedEuIds.length,
                  defaultValue: 'Clear {{count}} selected',
                })}
              </Button>
            )}
            <div className="hidden lg:block lg:flex-1" />
            {/* Export is visible to everyone; the action itself is Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleExportClick}
              disabled={results.length === 0}
            >
              <Download className="h-3 w-3 shrink-0" />
              {t('search.exportExcel')}
            </Button>
            {/* Enrich buttons are visible to everyone; the actions are Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleEnrichWebsites}
              disabled={isEnrichingWebsites || results.length === 0}
            >
              <Globe className="h-3 w-3 shrink-0" />
              {isEnrichingWebsites
                ? t('search.enriching')
                : t('search.enrichWebsites')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleEnrichEmployees}
              disabled={isEnrichingEmployees || results.length === 0}
            >
              <Users className="h-3 w-3 shrink-0" />
              {isEnrichingEmployees
                ? t('search.enriching')
                : t('search.enrichEmployees')}
            </Button>
          </div>
        </div>
  );
}
